<template>
  <div class="iframe-editor-container">
    <iframe
      ref="editorIframe"
      :src="editorUrl"
      class="editor-iframe"
    ></iframe>
  </div>
</template>

<script>
import { uploadMiniGameConfig, uploadMiniGameMap, getMiniGameResources } from '@/net/api';
import { resolveMediaUrl } from '@/utils/media';

const DECOR_TYPE_CONFIGS = [
  { key: 'houseTypes', folder: 'house', label: 'house' },
  { key: 'pedestrianTypes', folder: 'pedestrian', label: 'pedestrian' },
  { key: 'npcVehicleTypes', folder: 'npc_vehicle', label: 'npcVehicle' }
];

export default {
  name: 'MiniGameLevelEditor',
  props: {
    mapUrl: String,
    gameType: String,
    initialConfig: [Object, String],
    initialAssets: [Object, String]
  },
  data() {
    return {
      currentMapUrl: this.mapUrl,
      resources: [],
      isSaving: false,
    };
  },
  computed: {
    editorUrl() {
      if (this.gameType === 'drive_truck') {
        return '/drive-editor.html';
      }
      return '/map-editor.html';
    }
  },
  mounted() {
    window.addEventListener('message', this.handleMessage);
    this.resourcesPromise = this.loadResources();
  },
  beforeDestroy() {
    window.removeEventListener('message', this.handleMessage);
  },
  methods: {
    async loadResources() {
      try {
        const { data } = await getMiniGameResources();
        const resolvedResources = (data || []).map(res => {
          if (res.config && Array.isArray(res.config)) {
            res.config.forEach(group => {
              if (group.images && Array.isArray(group.images)) {
                group.images = group.images.map(img => resolveMediaUrl(img));
              }
            });
          }
          return res;
        });
        this.resources = resolvedResources;
      } catch (e) {
        console.error('Failed to load resources:', e);
      }
    },
    handleIframeLoad() {
      let config = this.initialConfig;
      if (typeof config === 'string') {
        try { config = JSON.parse(config); } catch (e) { config = {}; }
      }
      let assets = this.initialAssets;
      if (typeof assets === 'string') {
        try { assets = JSON.parse(assets); } catch (e) { assets = {}; }
      }

      // Resolve asset URLs
      const resolvedAssets = {};
      if (assets) {
        for (let rel in assets) {
          resolvedAssets[rel] = resolveMediaUrl(assets[rel]);
        }
      }

      // 修复 config 中可能残留的虚拟路径（如 "map/bgimg_xxx.png"）
      // 如果有 assets 映射就恢复成真实 URL，否则删除不可恢复的条目
      if (config.bgLibrary && resolvedAssets) {
        for (let id in config.bgLibrary) {
          const bg = config.bgLibrary[id];
          if (!bg) continue;
          if (bg.dataUrl && bg.dataUrl.startsWith('map/')) {
            const realUrl = resolvedAssets[bg.dataUrl];
            if (realUrl) {
              bg.dataUrl = realUrl;
              bg.path = bg.dataUrl;
            } else {
              // 无法恢复，从库中移除
              delete config.bgLibrary[id];
            }
          }
        }
      }
      // 修复 obstacleTypes 中的图片路径
      if (config.obstacleTypes) {
        for (let id in config.obstacleTypes) {
          const type = config.obstacleTypes[id];
          if (!type || !type.config) continue;
          type.config.forEach(item => {
            if (!item.images) return;
            for (let i = 0; i < item.images.length; i++) {
              const img = item.images[i];
              if (img && img.startsWith('obstacle/') && resolvedAssets[img]) {
                item.images[i] = resolvedAssets[img];
              }
            }
          });
        }
      }
      // 修复 itemTypes 中的图片路径
      if (config.itemTypes) {
        for (let id in config.itemTypes) {
          const type = config.itemTypes[id];
          if (!type || !type.config) continue;
          type.config.forEach(item => {
            if (!item.images) return;
            for (let i = 0; i < item.images.length; i++) {
              const img = item.images[i];
              if (img && img.startsWith('pickup/') && resolvedAssets[img]) {
                item.images[i] = resolvedAssets[img];
              }
            }
          });
        }
      }
      // 修复房子 / 行人 / 社会车辆类型中的图片路径
      for (const { key, folder } of DECOR_TYPE_CONFIGS) {
        if (!config[key]) continue;
        for (let id in config[key]) {
          const type = config[key][id];
          if (!type || !type.config) continue;
          type.config.forEach(item => {
            if (!item.images) return;
            for (let i = 0; i < item.images.length; i++) {
              const img = item.images[i];
              if (img && img.startsWith(folder + '/') && resolvedAssets[img]) {
                item.images[i] = resolvedAssets[img];
              }
            }
          });
        }
      }
      // 修复 playerResource 中的图片路径 (car/{configIndex}/{n}.png)
      if (config.playerResource && config.playerResource.config) {
        config.playerResource.config.forEach(item => {
          if (!item.images) return;
          for (let i = 0; i < item.images.length; i++) {
            const img = item.images[i];
            if (img && img.startsWith('car/') && resolvedAssets[img]) {
              item.images[i] = resolvedAssets[img];
            }
          }
        });
      }
      // 修复 finishLine 中的图片路径 (finish/{configIndex}/{n}.png)
      if (config.finishLine && config.finishLine.config) {
        config.finishLine.config.forEach(item => {
          if (!item.images) return;
          for (let i = 0; i < item.images.length; i++) {
            const img = item.images[i];
            if (img && img.startsWith('finish/') && resolvedAssets[img]) {
              item.images[i] = resolvedAssets[img];
            }
          }
        });
      }

      const mapUrl = resolveMediaUrl(this.mapUrl);

      console.log('Sending init message to iframe', { config, assets: resolvedAssets, mapUrl });

      this.$refs.editorIframe.contentWindow.postMessage({
        type: 'init',
        data: {
          config,
          assets: resolvedAssets,
          mapUrl,
          resources: this.resources
        }
      }, '*');
    },
    async handleMessage(event) {
      if (!this.$refs.editorIframe || event.source !== this.$refs.editorIframe.contentWindow) {
        return;
      }
      const { type, data } = event.data || {};
      try {
        if (type === 'ready') {
          if (this.resourcesPromise) {
            await this.resourcesPromise;
          }
          this.handleIframeLoad();
        } else if (type === 'cancel') {
          this.$emit('cancel');
        } else if (type === 'upload_image') {
          try {
            const res = await uploadMiniGameMap(data);
            this.currentMapUrl = res.data?.url;
            console.log('Map uploaded from editor', res.url);
          } catch (e) {
            this.$message.error("上传图片失败");
          }
        } else if (type === 'save') {
          if (this.isSaving) return;
          this.isSaving = true;
          await this.handleSaveFromEditor(data);
        }
      } catch (e) {
        console.error('Editor message handler error:', e);
      }
    },
    async handleSaveFromEditor(data) {
        console.log('======= [1] 准备保存到服务器 =======');
        console.log('Received save message from iframe, bgLibrary entries:', Object.keys(data.bgLibrary || {}).length, 'obstacleTypes:', Object.keys(data.obstacleTypes || {}).length, 'itemTypes:', Object.keys(data.itemTypes || {}).length);
        // 打印每个 bg 的 dataUrl 前缀用于调试
        if (data.bgLibrary) {
          for (let id in data.bgLibrary) {
            const bg = data.bgLibrary[id];
            const prefix = bg.dataUrl ? bg.dataUrl.substring(0, 50) : 'undefined';
            console.log(`  bg[${id}].dataUrl = "${prefix}..."`);
          }
        }
        const loadingMsg = this.$message({
          message: "正在处理并上传资源，请稍候...",
          type: 'info',
          duration: 0
        });

        try {
          const processedData = JSON.parse(JSON.stringify(data));
          const uploadCache = new Map();
          const assetsMap = {};

          // 辅助函数：将 Base64 转换为 File 对象进行上传
          const base64ToFile = (base64, filename) => {
            const arr = base64.split(',');
            const mime = arr[0].match(/:(.*?);/)[1];
            const bstr = atob(arr[1]);
            let n = bstr.length;
            const u8arr = new Uint8Array(n);
            while (n--) {
              u8arr[n] = bstr.charCodeAt(n);
            }
            return new File([u8arr], filename, { type: mime });
          };

          const stripApi = (url) => {
            if (url && url.startsWith('/api/')) return url.substring(4);
            return url;
          };

          const ensureApiUrl = (url) => {
            if (!url) return url;
            if (url.startsWith('/api/')) return url;
            if (url.startsWith('/uploadimgs/') || url.startsWith('/mini-game-maps/') ||
                url.startsWith('/uploadvideos/') || url.startsWith('/tempimgs/'))
              return '/api' + url;
            return url;
          };

          let totalUploads = 0;
          let completedUploads = 0;
          const onUploadComplete = (label) => {
            completedUploads++;
            console.log(`  [上传进度] ${label} (${completedUploads}/${totalUploads})`);
          };

          const uploadImageWithCache = async (base64, filename, label) => {
            if (uploadCache.has(base64)) {
              console.log(`  [缓存命中] ${label} -> ${uploadCache.get(base64)}`);
              return { url: uploadCache.get(base64) };
            }
            console.log(`  [3] 开始上传图片: ${label} (filename=${filename}, base64长度=${base64.length})`);
            const file = base64ToFile(base64, filename);
            const axiosRes = await uploadMiniGameMap(file);
            const uploadedUrl = axiosRes.data?.url;
            console.log(`  [3] 图片上传完成: ${label} -> ${uploadedUrl} (full res.data=`, axiosRes.data, `)`);
            uploadCache.set(base64, uploadedUrl);
            onUploadComplete(label);
            return { url: uploadedUrl };
          };

          // 获取当前已有的 assets (如果存在)
          let oldAssets = {};
          if (this.initialAssets) {
             if (typeof this.initialAssets === 'string') {
               try { oldAssets = JSON.parse(this.initialAssets); } catch(e){}
             } else {
               oldAssets = this.initialAssets;
             }
          }
          console.log('[2] 开始处理图片，oldAssets:', JSON.stringify(oldAssets).substring(0, 200));

          // 1. 处理背景图库 (bgLibrary)
          // bg.dataUrl 保持 /api 开头的完整 URL，iframe 可直接加载
          if (processedData.bgLibrary) {
            for (let id in processedData.bgLibrary) {
              const bg = processedData.bgLibrary[id];
              if (!bg) continue;
              const relPath = `map/${id}.png`;
              if (bg.dataUrl && bg.dataUrl.startsWith('data:')) {
                totalUploads++;
              }
            }
            for (let id in processedData.bgLibrary) {
              const bg = processedData.bgLibrary[id];
              if (!bg) continue;
              const relPath = `map/${id}.png`;
              if (bg.dataUrl && bg.dataUrl.startsWith('data:')) {
                console.log(`  [开始上传BG] ${bg.name}`);
                const res = await uploadImageWithCache(bg.dataUrl, bg.name || 'bg.png', `bg:${bg.name}`);
                assetsMap[relPath] = stripApi(res.url);
                bg.dataUrl = res.url;
                bg.path = relPath;
                console.log(`  [BG处理完成] id=${id}, dataUrl=${res.url}`);
              } else if (bg.dataUrl && bg.dataUrl.startsWith('map/') && oldAssets[bg.dataUrl]) {
                const realUrl = ensureApiUrl(oldAssets[bg.dataUrl]);
                console.log(`  [恢复旧BG] id=${id}, 虚拟路径="${bg.dataUrl}", 映射到="${realUrl}"`);
                assetsMap[relPath] = stripApi(realUrl);
                bg.dataUrl = realUrl;
                bg.path = relPath;
              } else if (bg.dataUrl && bg.dataUrl !== '' && !bg.dataUrl.startsWith('map/')) {
                console.log(`  [保留BG] id=${id}, dataUrl="${bg.dataUrl}" (确保/api前缀: ${ensureApiUrl(bg.dataUrl)})`);
                assetsMap[relPath] = stripApi(bg.dataUrl);
                bg.dataUrl = ensureApiUrl(bg.dataUrl);
                bg.path = relPath;
              } else {
                console.warn(`  [删除损坏BG] id=${id}, dataUrl="${bg.dataUrl}", 无assets映射可恢复`);
                delete processedData.bgLibrary[id];
              }
            }
          }

          // 2. 处理路障类型 (obstacleTypes) — config-based (obstacle/{id}/{configIdx+1}/{imgIdx+1}.png)
          if (processedData.obstacleTypes) {
            for (let id in processedData.obstacleTypes) {
              const type = processedData.obstacleTypes[id];
              if (!type) continue;
              if (type.config && Array.isArray(type.config)) {
                for (let ci = 0; ci < type.config.length; ci++) {
                  const item = type.config[ci];
                  if (item.images && Array.isArray(item.images)) {
                    for (let i = 0; i < item.images.length; i++) {
                      if (item.images[i] && item.images[i].startsWith('data:')) totalUploads++;
                    }
                  }
                }
              }
            }
            console.log(`[2] 路障类型统计: 需上传 ${totalUploads - completedUploads} 张, obstacleTypes keys: ${Object.keys(processedData.obstacleTypes).join(',')}`);
            for (let id in processedData.obstacleTypes) {
              const type = processedData.obstacleTypes[id];
              if (!type) continue;
              if (type.config && Array.isArray(type.config)) {
                for (let ci = 0; ci < type.config.length; ci++) {
                  const item = type.config[ci];
                  if (!item.images || !Array.isArray(item.images)) continue;
                  for (let i = 0; i < item.images.length; i++) {
                    const imgData = item.images[i];
                    if (!imgData) continue;
                    const relPath = `obstacle/${id}/${ci + 1}/${i + 1}.png`;
                    if (imgData.startsWith('data:')) {
                      const uploadRes = await uploadImageWithCache(imgData, `${id}_${ci}_${i}.png`, `obs:${id}[${ci}][${i}]`);
                      assetsMap[relPath] = stripApi(uploadRes.url);
                    } else if (imgData.startsWith('/uploadimgs/') || imgData.startsWith('/api/uploadimgs/') ||
                               imgData.startsWith('/mini-game-maps/') || imgData.startsWith('/api/mini-game-maps/')) {
                      assetsMap[relPath] = stripApi(imgData);
                    } else if (imgData.startsWith('obstacle/')) {
                      if (oldAssets[imgData]) {
                        assetsMap[imgData] = stripApi(oldAssets[imgData]);
                      }
                    } else {
                      assetsMap[relPath] = stripApi(imgData);
                    }
                    item.images[i] = relPath;
                  }
                }
              }
            }
          }

          // 3. 处理拾取物类型 (itemTypes) — config-based (pickup/{id}/{configIdx+1}/{imgIdx+1}.png)
          if (processedData.itemTypes) {
            for (let id in processedData.itemTypes) {
              const type = processedData.itemTypes[id];
              if (!type) continue;
              if (type.config && Array.isArray(type.config)) {
                for (let ci = 0; ci < type.config.length; ci++) {
                  const item = type.config[ci];
                  if (item.images && Array.isArray(item.images)) {
                    for (let i = 0; i < item.images.length; i++) {
                      if (item.images[i] && item.images[i].startsWith('data:')) totalUploads++;
                    }
                  }
                }
              }
            }
            console.log(`[2] 拾取物类型统计: 需上传 ${totalUploads - completedUploads} 张`);
            for (let id in processedData.itemTypes) {
              const type = processedData.itemTypes[id];
              if (!type) continue;
              if (type.config && Array.isArray(type.config)) {
                for (let ci = 0; ci < type.config.length; ci++) {
                  const item = type.config[ci];
                  if (!item.images || !Array.isArray(item.images)) continue;
                  for (let i = 0; i < item.images.length; i++) {
                    const imgData = item.images[i];
                    if (!imgData) continue;
                    const relPath = `pickup/${id}/${ci + 1}/${i + 1}.png`;
                    if (imgData.startsWith('data:')) {
                      const uploadRes = await uploadImageWithCache(imgData, `${id}_${ci}_${i}.png`, `item:${id}[${ci}][${i}]`);
                      assetsMap[relPath] = stripApi(uploadRes.url);
                    } else if (imgData.startsWith('/uploadimgs/') || imgData.startsWith('/api/uploadimgs/') ||
                               imgData.startsWith('/mini-game-maps/') || imgData.startsWith('/api/mini-game-maps/')) {
                      assetsMap[relPath] = stripApi(imgData);
                    } else if (imgData.startsWith('pickup/')) {
                      if (oldAssets[imgData]) {
                        assetsMap[imgData] = stripApi(oldAssets[imgData]);
                      }
                    } else {
                      assetsMap[relPath] = stripApi(imgData);
                    }
                    item.images[i] = relPath;
                  }
                }
              }
            }
          }

          // 3.5 处理房子 / 行人 / 社会车辆类型
          for (const { key, folder, label } of DECOR_TYPE_CONFIGS) {
            if (!processedData[key]) continue;
            for (let id in processedData[key]) {
              const type = processedData[key][id];
              if (!type || !type.config || !Array.isArray(type.config)) continue;
              for (let ci = 0; ci < type.config.length; ci++) {
                const item = type.config[ci];
                if (!item.images || !Array.isArray(item.images)) continue;
                for (let i = 0; i < item.images.length; i++) {
                  if (item.images[i] && item.images[i].startsWith('data:')) totalUploads++;
                }
              }
            }
            console.log(`[2] ${label} 类型统计: 需上传 ${totalUploads - completedUploads} 张`);
            for (let id in processedData[key]) {
              const type = processedData[key][id];
              if (!type || !type.config || !Array.isArray(type.config)) continue;
              for (let ci = 0; ci < type.config.length; ci++) {
                const item = type.config[ci];
                if (!item.images || !Array.isArray(item.images)) continue;
                for (let i = 0; i < item.images.length; i++) {
                  const imgData = item.images[i];
                  if (!imgData) continue;
                  const relPath = `${folder}/${id}/${ci + 1}/${i + 1}.png`;
                  if (imgData.startsWith('data:')) {
                    const uploadRes = await uploadImageWithCache(imgData, `${id}_${ci}_${i}.png`, `${label}:${id}[${ci}][${i}]`);
                    assetsMap[relPath] = stripApi(uploadRes.url);
                  } else if (imgData.startsWith('/uploadimgs/') || imgData.startsWith('/api/uploadimgs/') ||
                             imgData.startsWith('/mini-game-maps/') || imgData.startsWith('/api/mini-game-maps/')) {
                    assetsMap[relPath] = stripApi(imgData);
                  } else if (imgData.startsWith(folder + '/')) {
                    if (oldAssets[imgData]) {
                      assetsMap[imgData] = stripApi(oldAssets[imgData]);
                    }
                  } else {
                    assetsMap[relPath] = stripApi(imgData);
                  }
                  item.images[i] = relPath;
                }
              }
            }
          }

          // 4. 处理玩家资源图 (car/{configIndex}/{n}.png)
          if (processedData.playerResource && processedData.playerResource.config) {
            const pr = processedData.playerResource;
            for (let ci = 0; ci < pr.config.length; ci++) {
              const item = pr.config[ci];
              if (!item.images || !Array.isArray(item.images)) continue;
              for (let i = 0; i < item.images.length; i++) {
                const imgData = item.images[i];
                if (!imgData) continue;
                const relPath = 'car/' + (ci + 1) + '/' + (i + 1) + '.png';
                if (imgData.startsWith('data:')) {
                  const uploadRes = await uploadImageWithCache(imgData, 'player_' + ci + '_' + i + '.png', 'player:item' + ci + '[' + i + ']');
                  assetsMap[relPath] = stripApi(uploadRes.url);
                } else if (imgData.startsWith('/uploadimgs/') || imgData.startsWith('/api/uploadimgs/') ||
                           imgData.startsWith('/mini-game-maps/') || imgData.startsWith('/api/mini-game-maps/')) {
                  assetsMap[relPath] = stripApi(imgData);
                } else if (imgData.startsWith('car/')) {
                  if (oldAssets[imgData]) {
                    assetsMap[imgData] = stripApi(oldAssets[imgData]);
                  }
                } else {
                  assetsMap[relPath] = stripApi(imgData);
                }
                item.images[i] = relPath;
              }
            }
          }

          // 5. 处理终点资源图 (finish/{configIndex}/{n}.png)
          if (processedData.finishLine && processedData.finishLine.config) {
            const fl = processedData.finishLine;
            for (let ci = 0; ci < fl.config.length; ci++) {
              const item = fl.config[ci];
              if (!item.images || !Array.isArray(item.images)) continue;
              for (let i = 0; i < item.images.length; i++) {
                const imgData = item.images[i];
                if (!imgData) continue;
                const relPath = 'finish/' + (ci + 1) + '/' + (i + 1) + '.png';
                if (imgData.startsWith('data:')) {
                  const uploadRes = await uploadImageWithCache(imgData, 'finish_' + ci + '_' + i + '.png', 'finish:item' + ci + '[' + i + ']');
                  assetsMap[relPath] = stripApi(uploadRes.url);
                } else if (imgData.startsWith('/uploadimgs/') || imgData.startsWith('/api/uploadimgs/') ||
                           imgData.startsWith('/mini-game-maps/') || imgData.startsWith('/api/mini-game-maps/')) {
                  assetsMap[relPath] = stripApi(imgData);
                } else if (imgData.startsWith('finish/')) {
                  if (oldAssets[imgData]) {
                    assetsMap[imgData] = stripApi(oldAssets[imgData]);
                  }
                } else {
                  assetsMap[relPath] = stripApi(imgData);
                }
                item.images[i] = relPath;
              }
            }
          }

          console.log(`[4] 所有图片处理完成! 共上传 ${completedUploads} 张图片`);
          // 保留未改动的历史资源映射
          for (const [key, url] of Object.entries(oldAssets)) {
            if (!assetsMap[key] && url) {
              assetsMap[key] = stripApi(url);
            }
          }
          console.log('[4] assetsMap:', JSON.stringify(assetsMap).substring(0, 300));
          // 打印清理后 bgLibrary 中每个条目的 dataUrl
          if (processedData.bgLibrary) {
            for (let id in processedData.bgLibrary) {
              console.log(`  [保存数据] bg[${id}].dataUrl = "${processedData.bgLibrary[id].dataUrl}"`);
            }
          }

          // 4. 上传最终清洗后的 JSON
          console.log('[5] 正在提交配置到服务器...');
          const res = await uploadMiniGameConfig(processedData);
          console.log('[6] 服务器已保存, 返回结果:', res);

          if (loadingMsg) loadingMsg.close();
          this.$emit('save', {
            config: processedData,
            assets: assetsMap,
            map_image: this.currentMapUrl,
            config_url: res.data?.url
          });
          console.log('======= 保存流程结束 =======');
        } catch (e) {
          if (loadingMsg) loadingMsg.close();
          console.error('保存流程出现异常:', e);
          this.$message.error("保存失败或上传资源出错");
        } finally {
          this.isSaving = false;
        }
    }
  }
};
</script>

<style scoped>
.iframe-editor-container {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #1a1a2e;
}
.editor-iframe {
  flex: 1;
  border: none;
  width: 100%;
  height: 100%;
}
</style>
